import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { ToastLink, toast } from '@planera/ui-components/feedback';
import { css } from '@styled/css';
import { useCallback, useEffect, useRef } from 'react';
import semver from 'semver';
import { useRegisterSW } from 'virtual:pwa-register/react';

import './RefreshAppToast.scss';

import { isPuppeteerPdfRoute } from 'helpers/isPuppeteerPdfRoute';
import { AUTOMATION_IDS } from 'state_management/automationIds';
import { useFeatureFlags } from 'state_management/featureFlags';

const MINUTES = 1; // Every minute check if there is a new version
const MS_PER_MINUTE = 60 * 1000;
const NEW_VERSION_CHECK_INTERVAL = MINUTES * MS_PER_MINUTE;

function RefreshAppToast() {
  const { minimumAppVersion } = useFeatureFlags();
  const currentPackageVersion = import.meta.env.VITE_PACKAGE_VERSION;
  const validCurrentVersion = currentPackageVersion ? semver.valid(currentPackageVersion) : null;
  const minimumVersion = minimumAppVersion ? semver.valid(minimumAppVersion) : null;
  const forceRefresh = Boolean(validCurrentVersion && minimumVersion && semver.lt(validCurrentVersion, minimumVersion));
  const toastRef = useRef<ReturnType<typeof toast> | null>(null);
  const swUpdateIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearToastRef = useCallback(() => {
    toastRef.current = null;
  }, []);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker
  } = useRegisterSW({
    onRegistered(r) {
      if (r) {
        if (swUpdateIntervalRef.current) {
          clearInterval(swUpdateIntervalRef.current);
        }
        swUpdateIntervalRef.current = setInterval(() => {
          void r.update();
        }, NEW_VERSION_CHECK_INTERVAL);
      }
    },
    onRegisterError(error) {
      logger.error('SW registration error', { error });
    }
  });

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (swUpdateIntervalRef.current) {
        clearInterval(swUpdateIntervalRef.current);
        swUpdateIntervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!needRefresh || forceRefresh || isPuppeteerPdfRoute) return;

    if (!toastRef.current) {
      toastRef.current = toast({
        title: 'A new version of Planera is available',
        dismissible: false,
        duration: 0,
        link: (
          <ToastLink
            data-testid={AUTOMATION_IDS.STATEFUL_APP.REFRESH_TOAST_LINK}
            href="#"
            onClick={(e) => {
              e.preventDefault();
              void updateServiceWorker(true);
            }}>
            Refresh
          </ToastLink>
        ),
        onClose: clearToastRef,
        persistent: true,
        position: 'bottom',
        type: 'new',
        variant: 'inverse'
      });
    }

    return () => {
      toastRef.current?.dismiss({ silent: true });
      toastRef.current = null;
    };
  }, [needRefresh, forceRefresh, updateServiceWorker, clearToastRef]);

  if (isPuppeteerPdfRoute || !forceRefresh) {
    return null;
  }

  return (
    <Dialog
      open={needRefresh}
      className="refreshAppDialog"
      data-testid={AUTOMATION_IDS.STATEFUL_APP.REFRESH_DIALOG}>
      <DialogTitle data-testid={AUTOMATION_IDS.STATEFUL_APP.REFRESH_DIALOG_TITLE}>
        <span
          className={css({
            w: '24px',
            h: '24px',
            bg: 'transparent url(/src/assets/icons/new-version-green.svg) no-repeat center center',
            bgSize: '24px 24px'
          })}
          data-testid={AUTOMATION_IDS.STATEFUL_APP.REFRESH_DIALOG_ICON}
        />
        <span>New Version Available</span>
      </DialogTitle>
      <DialogContent data-testid={AUTOMATION_IDS.STATEFUL_APP.REFRESH_DIALOG_CONTENT}>All your changes have been saved and will not be lost</DialogContent>
      <DialogActions data-testid={AUTOMATION_IDS.STATEFUL_APP.REFRESH_DIALOG_ACTIONS}>
        <Button
          onClick={() => updateServiceWorker(true)}
          autoFocus
          data-testid={AUTOMATION_IDS.STATEFUL_APP.REFRESH_DIALOG_BUTTON}>
          Refresh
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RefreshAppToast;
